"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.printDependencyGraph = exports.getIndependentContainers = exports.removeFromDependencyGraph = exports.addToDependencyGraph = void 0;
const focusContextManager_1 = require("./focusContextManager");
/**
 * @returns Whether container a is dependent on container b based on their zIndices
 * a is dependent on b iff a's zIndex < b's zIndex
 */
const dependentFromZIndex = (a, b) => {
    return a.getZIndex() < b.getZIndex();
};
/**
 * @returns Whether container a is dependent on container b based on their hierarchy
 * a is dependent on b iff a is contained within b
 */
const dependentFromHierarchy = (a, b) => {
    const aElement = a.getElement();
    const bElement = b.getElement();
    if (!aElement || !bElement) {
        return false;
    }
    return aElement.contains(bElement);
};
// Initialize an empty graph
const dependencyGraph = new Map();
/**
 * Adds a given focus container to the dependency graph
 *
 * @param containerToAdd The container to add to the dependency graph
 */
const addToDependencyGraph = (container) => {
    var _a, _b;
    // If the container is not already in the graph, add it.
    if (!dependencyGraph.has(container)) {
        dependencyGraph.set(container, new Set());
    }
    // Check existing nodes in the graph for dependencies
    for (const existingContainer of (0, focusContextManager_1.getFocusedContainers)()) {
        if (container === existingContainer) {
            continue;
        }
        const existingDependsOnContainerHierarchy = dependentFromHierarchy(existingContainer, container);
        const containerDependsOnExistingHierarchy = dependentFromHierarchy(container, existingContainer);
        if (containerDependsOnExistingHierarchy ||
            (dependentFromZIndex(container, existingContainer) && !existingDependsOnContainerHierarchy)) {
            // Add existingContainer as a dependency for container
            (_a = dependencyGraph.get(container)) === null || _a === void 0 ? void 0 : _a.add(existingContainer);
        }
        else if (existingDependsOnContainerHierarchy ||
            (dependentFromZIndex(existingContainer, container) && !containerDependsOnExistingHierarchy)) {
            // Add container as a dependency for existingContainer
            (_b = dependencyGraph.get(existingContainer)) === null || _b === void 0 ? void 0 : _b.add(container);
        }
    }
};
exports.addToDependencyGraph = addToDependencyGraph;
/**
 * Removes a given focus container from the dependency graph
 *
 * @param containerToRemove The container to remove from the dependency graph
 */
const removeFromDependencyGraph = (container) => {
    dependencyGraph.delete(container);
    for (const [, dependencies] of dependencyGraph) {
        dependencies.delete(container);
    }
};
exports.removeFromDependencyGraph = removeFromDependencyGraph;
/**
 * Returns the list of graph nodes without dependencies
 */
const getIndependentContainers = () => {
    const containers = [];
    for (const [container, dependencies] of dependencyGraph) {
        if (dependencies.size === 0) {
            containers.push(container);
        }
    }
    return containers;
};
exports.getIndependentContainers = getIndependentContainers;
/**
 * A helper function for generating and printing a string representation of a
 * focus container dependency graph
 */
const printDependencyGraph = () => {
    const nodes = [];
    for (const [container, dependencies] of dependencyGraph.entries()) {
        const containerName = container.getName();
        const dependencyNames = Array.from(dependencies).map((dependency) => dependency.getName());
        nodes.push(`${containerName} : [${dependencyNames.join(', ')}]`);
    }
    console.log(`[${nodes.join(', ')}]`);
};
exports.printDependencyGraph = printDependencyGraph;
//# sourceMappingURL=data:application/json;base64,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